import { useSnackbar } from 'notistack';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { openLogin, openBalance } from '../../redux/slices/authUiSlice';
import Image from '../Image';
import useAuth from '../../hooks/useAuth';

// Assets
import HomeClassic from '../../assets/navigation/home-classic.png';
import HomeHover from '../../assets/navigation/home-hover.png';
import GiftClassic from '../../assets/navigation/gift-classic.png';
import GiftHover from '../../assets/navigation/gift-hover.png';
import Settings from '../../assets/navigation/settings.png';
import HistoryHover from '../../assets/navigation/history-hover.png';
import HistoryClassic from '../../assets/navigation/history-classic.png';
import Deposit from '../../assets/navigation/deposit.png';
import DepositHover from '../../assets/navigation/deposit-hover.png';
import SlotClassic from '../../assets/navigation/slot-classic.png';
import SlotHover from '../../assets/navigation/slot-hover.png';
import AcewinClassic from '../../assets/navigation/acewin-classic.png';
import AcewinHover from '../../assets/navigation/acewin-hover.png';
import CasinoClassic from '../../assets/navigation/live-casino-classic.png';
import CasinoHover from '../../assets/navigation/live-casino-hover.png';
import SportClassic from '../../assets/navigation/sports-classic.png';
import SportHover from '../../assets/navigation/sport-hover.png';
import InstantGameHover from '../../assets/navigation/instant-game-hover.png';
import InstantGameClassic from '../../assets/navigation/instant-game-classic.png';
import EgamesHover from '../../assets/navigation/egames-hover.png';
import EgamesClassic from '../../assets/navigation/egames-classic.png';
import FishClassic from '../../assets/navigation/fish-classic.png';
import FishHover from '../../assets/navigation/fish-hover.png';
import Crown from '../../assets/navigation/crown-classic.png';
import CrownHover from '../../assets/navigation/crown-hover.png';
import BalanceClassic from '../../assets/navigation/balance-classic.png';
import BalanceHover from '../../assets/navigation/balance-hover.png';
import Logo from '../../assets/brand-logo.png';
import Bank from '../../assets/settings/bank.png';
import Account from '../../assets/settings/user.png';
import Referral from '../../assets/settings/referral.png';
import Logout from '../../assets/settings/logout.png';
import History2 from '../../assets/navigation/clock.png';

import useLocales from '../../hooks/useLocales';
import conObj from '../../utils/connection-assistant';
import { dispatch, useSelector } from '../../redux/store';
import { closeLoadingScreen, openLoadingScreen } from '../../redux/slices/settings';
import { openConfirm } from '../../redux/slices/confirm';

// --- Sub-components ---

const MenuBox = ({ onClick, title, icon }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      padding: '8px 12px',
      gap: '10px',
      borderBottom: '1px solid #333',
      cursor: 'pointer',
      '&:hover': { backgroundColor: '#444' },
    }}
  >
    <Image sx={{ width: 18, height: 18 }} src={icon} />
    <Typography fontSize={'12px'} color="#FFF">
      {title}
    </Typography>
  </Box>
);

const NavigationComponent = ({ onClick, title, classicIcon, classicHoverIcon, active, navigateTo }) => {
  const [isHover, setIsHover] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { translate } = useLocales();

  const isHome = navigateTo === '/';

  const isActive =
    active || (isHome ? location.pathname === '/' : navigateTo && location.pathname.startsWith(navigateTo));

  const hovered = isHover || isActive;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (navigateTo) {
      navigate(navigateTo, { replace: true });
    }
  };

  return (
    <Box
      onClick={handleClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      sx={{
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.2vw 0.6vw',
        flexShrink: 1,
      }}
    >
      <Box
        component="img"
        src={hovered ? classicHoverIcon : classicIcon}
        alt={title}
        sx={{
          width: 'clamp(18px, 2vw, 25px)',
          height: 'clamp(18px, 2vw, 25px)',
          transform: hovered ? 'scale(1.1)' : 'scale(1)',
          transition: 'all 0.3s ease',
        }}
      />

      <Typography
        sx={{
          color: isActive || isHover ? '#ffcc00' : '#FFFFFF',
          fontSize: 'clamp(9px, 0.85vw, 13px)',
          fontWeight: 500,
          mt: '4px',
          transition: 'color 0.3s ease',
        }}
      >
        {translate(title)}
      </Typography>
    </Box>
  );
};

export const BannerHeader = () => {
  const { translate } = useLocales();
  const menuRef = useRef(null);

  const dispatchUi = useDispatch();

  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { logout, isAuthenticated } = useAuth();

  const { gameCategsAndCompanies } = useSelector((x) => x.lookup);

  const currentPath = decodeURIComponent(location.pathname);

  const [customArrangements, setCustomArrangements] = useState([]);

  const [openProfile, setOpenProfile] = useState(false);

  const [activeIndex, setActiveIndex] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const navItems = [
    {
      title: 'home',
      classicIcon: HomeClassic,
      classicHoverIcon: HomeHover,
      onClick: () => navigate('/', { replace: true }),
      navigateTo: '/',
    },
    {
      title: 'history',
      classicIcon: HistoryClassic,
      classicHoverIcon: HistoryHover,
      onClick: () => {
        if (!isAuthenticated) {
          dispatchUi(openLogin());
        } else {
          navigate('/history?tab=transaction');
        }
      },
      navigateTo: '/history',
    },
    {
      title: 'promotions',
      classicIcon: GiftClassic,
      classicHoverIcon: GiftHover,
      onClick: () => navigate('/promotion/tab', { replace: true }),
      navigateTo: '/promotion/tab',
    },
    {
      title: 'deposit',
      classicIcon: Deposit,
      classicHoverIcon: DepositHover,
      onClick: () => {
        if (!isAuthenticated) {
          dispatchUi(openLogin());
        } else {
          navigate('/user/wallet?tab=deposit');
        }
      },
      navigateTo: '/user/wallet',
    },
    {
      title: 'balance',
      classicIcon: BalanceClassic,
      classicHoverIcon: BalanceHover,
      onClick: () => {
        if (!isAuthenticated) {
          dispatchUi(openLogin());
        } else {
          dispatchUi(openBalance());
        }
      },
    },
  ];

  const classicIcons = [
    SlotClassic,
    CasinoClassic,
    SportClassic,
    FishClassic,
    AcewinClassic,
    InstantGameClassic,
    EgamesClassic,
    Crown,
  ];
  const hoverIcons = [
    SlotHover,
    CasinoHover,
    SportHover,
    FishHover,
    AcewinHover,
    InstantGameHover,
    EgamesHover,
    CrownHover,
  ];

  const customGameCategoriesData = useMemo(
    () => [
      ...(gameCategsAndCompanies?.map((item, index) => ({
        ...item,
        navigateTo: `/arrangement/${item.slug}`,
        classicIcon: classicIcons[index % classicIcons.length],
        hoverIcon: hoverIcons[index % hoverIcons.length],
      })) || []),
      // { hoverIcon: EgamesHover, classicIcon: EgamesClassic, name: 'E-Games', navigateTo: '/egames' },
      { hoverIcon: CrownHover, classicIcon: Crown, name: 'VIP', navigateTo: '/vip' },
    ],
    [gameCategsAndCompanies]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '80px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // overflow: 'hidden',
      }}
    >
      {/* Background Video Layer */}
      <Box
        component="video"
        loop
        autoPlay
        muted
        playsInline
        sx={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
      >
        <source src="https://media.88new11.com/public/new88/site-tong/786404ec-8b52-4ab8-8be3-3b74d181f00d.mp4" />
      </Box>

      {/* Content Layer: Perfectly Centered */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '1400px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center', // Centers everything in the row
          px: '1.5vw',
          gap: '0.5vw',
        }}
      >
        {/* Logo Section */}
        <Box sx={{ flexShrink: 2, display: 'flex', alignItems: 'center' }}>
          <Image
            onClick={() => navigate('/', { replace: true })}
            sx={{
              width: 'clamp(100px, 11vw, 150px)',
              height: 'auto',
              transition: 'width 0.3s ease',
              cursor: 'pointer',
            }}
            src={Logo}
            alt="Logo"
          />
        </Box>

        {/* Centered Navigation Items Group */}
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.2vw',
          }}
        >
          {navItems.map((item, index) => (
            <NavigationComponent key={`nav-${index}`} {...item} />
          ))}

          {customGameCategoriesData.map((cat, index) => (
            <NavigationComponent
              key={`cat-${index}`}
              title={cat.name}
              classicIcon={cat.classicIcon}
              classicHoverIcon={cat.hoverIcon}
              navigateTo={cat.navigateTo}
            />
          ))}
        </Box>

        {/* Settings Section (Right) */}
        <Box
          ref={menuRef}
          sx={{
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              if (!isAuthenticated) {
                dispatchUi(openLogin());
              } else {
                setShowMenu((prev) => !prev);
              }
            }}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: '0.5vw',
            }}
          >
            <Image sx={{ width: 'clamp(18px, 2vw, 25px)', height: 'clamp(18px, 2vw, 25px)' }} src={Settings} />
            <Typography sx={{ fontSize: 'clamp(9px, 0.85vw, 13px)', color: '#FFF', mt: '4px' }}>
              {translate('settings')}
            </Typography>
          </Box>

          {/* Dropdown Menu */}
          <Box
            className="menuBox"
            sx={{
              // Change: controlled by showMenu state instead of hover
              display: showMenu ? 'block' : 'none',
              position: 'absolute',
              top: '100%',
              right: 0,
              mt: '12px', // Added a bit more space for a cleaner look
              width: '180px',
              background: '#232734',
              border: '1px solid #ffcc00',
              borderRadius: '6px',
              overflow: 'hidden',
              zIndex: 1000,
              boxShadow: '0px 4px 20px rgba(0,0,0,0.5)', // Adds depth
            }}
          >
            <MenuBox
              onClick={() => {
                navigate('/user/account', { replace: true });
                setShowMenu(false);
              }}
              title={translate('account')}
              icon={Account}
            />
            <MenuBox
              onClick={() => {
                navigate('/user/banks', { replace: true });
                setShowMenu(false);
              }}
              title={translate('bank_account')}
              icon={Bank}
            />
            <MenuBox
              onClick={() => {
                navigate('/referral?tab=referral');
                setShowMenu(false);
              }}
              title={translate('referral')}
              icon={Referral}
            />
            <MenuBox
              onClick={() => {
                navigate('/history?tab=transaction');
                setShowMenu(false);
              }}
              title={translate('history')}
              icon={History2}
            />
            <MenuBox
              onClick={() => {
                dispatch(
                  openConfirm({
                    title: translate('logout_confirmation'),
                    subTitle: translate('are_you_sure_to_logout'),
                    onConfirm: handleLogout,
                  })
                );
                setShowMenu(false);
              }}
              title={translate('logout')}
              icon={Logout}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
